import type { MeetingAttachableType } from "@/schema"
import { Video } from "lucide-react"

import { useMeetingPreview } from "@/hooks/api/meetings/queries"
import { Link } from "@/components/book-link"

import { PreviewField } from "./preview-fields"

export function RecentMeetings({
  type,
  id,
  bookId,
  onNavigate,
}: {
  type: MeetingAttachableType
  id: string
  bookId?: string
  onNavigate?: () => void
}) {
  const { data, isPending, isError } = useMeetingPreview(type, id, bookId)

  if (!isPending && !isError && !data?.meetings.length) return null

  return (
    <div className="space-y-1.5 pt-2 pb-0.5">
      {isPending || isError || !data?.meetings.length ? (
        <PreviewField icon={Video} label="Meetings">
          {isPending ? "Loading meetings…" : isError ? "Unable to load meetings" : null}
        </PreviewField>
      ) : (
        data.meetings.map((meeting) => (
          <Link
            key={meeting.id}
            href={`/meetings/${meeting.id}`}
            bookIdOverride={meeting.book_id ?? bookId}
            className="text-tertiary-foreground -mx-1.5 flex min-w-0 items-start gap-1.5 rounded-md px-1.5 py-1 text-xs transition-colors"
            onClick={(event) => {
              if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) onNavigate?.()
            }}
          >
            <Video className="text-muted-foreground mt-0.5 size-3 shrink-0" aria-label="Meeting" />
            <div className="min-w-0">
              <div className="truncate font-medium">{meeting.title || "Untitled meeting"}</div>
              <div className="text-muted-foreground text-[11px]">
                {new Date(meeting.start_time).getTime() >= Date.now() && (
                  <span className="text-blue-foreground font-medium">Upcoming · </span>
                )}
                {new Date(meeting.start_time).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </div>
            </div>
          </Link>
        ))
      )}
    </div>
  )
}
